import { PDFDocument, StandardFonts, rgb } from "pdf-lib";
import { OfferForm } from "../types/OfferObject";
import fontkit from "@pdf-lib/fontkit";

/**
 * Appends project details and signatory pages to an existing SLA PDF.
 * Original SLA pages remain untouched.
 */
export async function augmentContractPdf(params: {
  pdf: File;
  offer: OfferForm;
  currency?: string;
}): Promise<File> {
  const { pdf, offer, currency = "₦" } = params;

  console.log("Augmenting: ", pdf.name, "Offer: ", offer)

  const pdfDoc = await PDFDocument.load(await pdf.arrayBuffer());
  pdfDoc.registerFontkit(fontkit);
  
   const fontByte = await fetch("/fonts/Inter_18pt-Regular.ttf").then(res => res.arrayBuffer())
   const fontByteBold = await fetch("/fonts/Inter_18pt-Bold.ttf").then(res => res.arrayBuffer())

  const font = await pdfDoc.embedFont(fontByte);
  const boldFont = await pdfDoc.embedFont(fontByteBold);

  /**
   * -------------------------
   * Project Details Page
   * -------------------------
   */
  const projectPage = pdfDoc.addPage();
  const { width, height } = projectPage.getSize();

  let y = height - 50;

  const drawText = (
    text: string,
    options?: { size?: number; bold?: boolean }
  ) => {
    const size = options?.size ?? 11;

    projectPage.drawText(text, {
      x: 50,
      y,
      size,
      font: options?.bold ? boldFont : font,
      color: rgb(0, 0, 0),
      maxWidth: width - 100,
      lineHeight: size + 4,
    });

    y -= size + 8;
  };

  // Header
  drawText("PROJECT DETAILS", { size: 16, bold: true });
  y -= 10;

  // Compute contract end date from startDate + agreed_duration
  const computedEndDate = (() => {
    const start = new Date(offer.startDate);
    const durationDays = parseInt(offer.agreed_duration ?? "0", 10);
    if (!isNaN(start.getTime()) && !isNaN(durationDays)) {
      const end = new Date(start);
      end.setDate(end.getDate() + durationDays);
      return end.toISOString().split("T")[0];
    }
    return "N/A";
  })();

  // Core contract metadata
  drawText(`Work Type: ${offer.work_type}`);
  drawText(`Payment Type: ${offer.payment_type}`);
  drawText(`Total Agreed Cost: ${currency}${offer.agreed_total_cost}`);
  drawText(`Start Date: ${offer.startDate}`);
  drawText(`End Date: ${computedEndDate}`);
  drawText(`Agreed Duration: ${offer.agreed_duration} days`);

  if (offer.work_type === "retainer") {
    drawText(`Billing Interval: ${offer.retainer_agreed_interval}`);
    drawText(
      `Interval Payment: ${currency}${offer.retainer_agreed_interval_payment}`
    );
  }

  y -= 10;

  // Description
  drawText("Scope Description", { bold: true });
  drawText(offer.description);

  // Milestones
  if (offer.milestones?.length > 0) {
    y -= 10;
    drawText("Milestones", { bold: true });

    offer.milestones.forEach((milestone, index) => {
      drawText(`${index + 1}. ${milestone.description}`);
      drawText(`   Amount: ${currency}${milestone.amountPayable}`);
      drawText(`   Timeline: ${milestone.startDate} → ${milestone.endDate}`);
      y -= 4;
    });
  }

  /**
   * -------------------------
   * Signatory Page
   * -------------------------
   */
  const signPage = pdfDoc.addPage();
  const { height: signHeight } = signPage.getSize();

  let sy = signHeight - 70;

  const drawSignLine = (label: string) => {
    signPage.drawText(label, {
      x: 50,
      y: sy,
      size: 11,
      font,
    });

    signPage.drawText("______________________________", {
      x: 200,
      y: sy,
      size: 11,
      font,
    });

    sy -= 40;
  };

  signPage.drawText("SIGNATORY PAGE", {
    x: 50,
    y: sy,
    size: 16,
    font: boldFont,
  });

  sy -= 60;

  drawSignLine("Client Name:");
  drawSignLine("Service Provider:");
  drawSignLine("Signature:");
  drawSignLine("Date:");

  /**
   * -------------------------
   * Final Output
   * -------------------------
   */
  return pdfBytesToFile(await pdfDoc.save(), pdf.name);
}

export function pdfBytesToFile(pdfBytes: Uint8Array, fileName: string): File {
  return new File([pdfBytes as Uint8Array<ArrayBuffer>], fileName, {
    type: "application/pdf",
  });
}
